import { Box, CircularProgress, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Context from '../Context'
import { useClock } from '../utils/clock'
import theme from '../theme'
import useInterval from '../utils/useInterval'
import { apiFetch } from '../utils/api'
import { americanToDecimal, americanToDecimalExact, formatPrice } from '../utils/price'
import { marginOf } from '../utils/margin'
import PriceHistoryModal from './PriceHistoryModal'

// Oswald is condensed and reads far too heavy at table sizes, the grid needs a plain sans
const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

const PERIOD_LABELS = {
  GAME: 'Game',
  FIRST_HALF: 'First Half',
  SECOND_HALF: 'Second Half',
  FIRST_Q: 'First Quarter',
  SECOND_Q: 'Second Quarter',
  THIRD_Q: 'Third Quarter',
  FOURTH_Q: 'Fourth Quarter'
}

const PERIOD_ORDER = ['GAME', 'FIRST_HALF', 'SECOND_HALF', 'FIRST_Q', 'SECOND_Q', 'THIRD_Q', 'FOURTH_Q']

// must match CANONICAL_BOOKMAKER in server/index.js: the history is stored under these names
const BOOKMAKER_LABELS = {
  'PINNAKLE API': 'Pinnacle',
  'LIVE PINNAKLE API': 'Pinnacle Live',
  'LAY BETFAIR API': 'Lay Betfair',
  'LIVE LAY BETFAIR API': 'Lay Betfair Live',
  'MATCHBOOK API': 'Matchbook'
}

const bookmakerLabel = (raw) => BOOKMAKER_LABELS[raw] || String(raw || '').replace(/\s*API$/i, '')

// "o 23.0" and a plain 23 have to produce one key, exactly as totalPoints() does on the server:
// the number is normalised, not the text taken verbatim
const totalPoints = (label) => {
  const match = /-?\d+(\.\d+)?/.exec(String(label == null ? '' : label))
  return match ? String(Number(match[0])) : null
}

const COL = {
  date: { width: '7%' },
  time: { width: '9%' },
  teams: { width: '18%' },
  spread: { width: '17%' },
  ml: { width: '9%' },
  total: { width: '18%' },
  teamTotals: { width: '22%' }
}

const cellSx = { paddingLeft: '12px', borderLeft: '1px solid #EEEEEE' }

const ladderSx = {
  display: 'grid',
  gridTemplateColumns: 'auto auto',
  justifyContent: 'start',
  columnGap: '12px',
  rowGap: '2px',
  fontSize: '13px',
  lineHeight: 1.45
}

const labelSx = { color: '#9A9EA3', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const priceSx = { fontWeight: 600, fontVariantNumeric: 'tabular-nums' }

const MOVE_TINT = {
  up: 'rgba(7, 176, 130, 0.22)',
  down: 'rgba(198, 30, 31, 0.20)'
}

/**
 * `info` carries everything the history screen needs about this one price: what it identifies, how to
 * name it, the margin of the market it sits in, and the key the book's max stake is looked up by.
 */
function Price({ value, usPrices, onOpen, info, movedOf }) {
  if (value === null || value === undefined || value === 0) {
    return <Box component="span" sx={{ color: '#C9CCCF' }}>—</Box>
  }
  const runnerKey = info && info.runnerKey
  const moved = runnerKey && movedOf ? movedOf(runnerKey) : null
  return (
    <Box
      component="span"
      title="Price history"
      onClick={() => onOpen && runnerKey && onOpen(info, value)}
      sx={{
        ...priceSx,
        cursor: onOpen && runnerKey ? 'pointer' : 'default',
        // a tint rather than the grid's side stripe: these prices are inline spans, not table cells
        backgroundColor: moved ? MOVE_TINT[moved] : 'transparent',
        borderRadius: '2px',
        padding: '0 3px',
        margin: '0 -3px',
        transition: 'background-color 0.4s',
        '&:hover': { textDecoration: onOpen && runnerKey ? 'underline' : 'none' }
      }}
    >
      {formatPrice(value, usPrices)}
    </Box>
  )
}

// the two or three prices of one market, as the book published them, are what the overround is read from
const marginOfRow = (...americanPrices) => marginOf(americanPrices.map(americanToDecimalExact))

function SpreadCol({ rows, names, usPrices, onOpen, movedOf }) {
  if (!rows || !rows.length) {
    return null
  }
  return (
    <Box sx={ladderSx}>
      {rows.flatMap((row, i) => {
        const margin = marginOfRow(row.homeTeamPrice, row.awayTeamPrice)
        return [
          <Box key={`hl${i}`} sx={labelSx}>{row.homeSpread ?? '—'}</Box>,
          <Price key={`hp${i}`} value={row.homeTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
            runnerKey: `SPREAD|HOME|${row.homeSpread}`,
            label: `Home spread ${row.homeSpread}`,
            market: 'Spread',
            selection: `${names.home} ${row.homeSpread}`,
            priceKey: row.homePriceKey,
            margin
          }} />,
          <Box key={`al${i}`} sx={labelSx}>{row.awaySpread ?? '—'}</Box>,
          <Price key={`ap${i}`} value={row.awayTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
            runnerKey: `SPREAD|AWAY|${row.awaySpread}`,
            label: `Away spread ${row.awaySpread}`,
            market: 'Spread',
            selection: `${names.away} ${row.awaySpread}`,
            priceKey: row.awayPriceKey,
            margin
          }} />
        ]
      })}
    </Box>
  )
}

function TotalCol({ rows, usPrices, onOpen, movedOf }) {
  if (!rows || !rows.length) {
    return null
  }
  return (
    <Box sx={ladderSx}>
      {rows.flatMap((row, i) => {
        const points = totalPoints(row.homeTotal ?? row.awayTotal)
        const margin = marginOfRow(row.homeTeamPrice, row.awayTeamPrice)
        return [
          <Box key={`ol${i}`} sx={labelSx}>{row.homeTotal ?? row.awayTotal ?? '—'}</Box>,
          <Price key={`op${i}`} value={row.homeTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
            runnerKey: `TOTAL|OVER|${points}`,
            label: `Over ${row.homeTotal ?? row.awayTotal}`,
            market: 'Total',
            selection: `Over ${points}`,
            priceKey: row.homePriceKey,
            margin
          }} />,
          <Box key={`ul${i}`} sx={labelSx}>{row.awayTotal ?? row.homeTotal ?? '—'}</Box>,
          <Price key={`up${i}`} value={row.awayTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
            runnerKey: `TOTAL|UNDER|${points}`,
            label: `Under ${row.awayTotal ?? row.homeTotal}`,
            market: 'Total',
            selection: `Under ${points}`,
            priceKey: row.awayPriceKey,
            margin
          }} />
        ]
      })}
    </Box>
  )
}

function MoneylineCol({ game, usPrices, onOpen, movedOf }) {
  const row = game.moneylineDisplayRow || game.threeWayMonelineRow
  if (!row) {
    return null
  }
  // the draw is a leg of the same market, so it belongs in the overround wherever the book offers it
  const margin = marginOfRow(row.homeTeamPrice, row.awayTeamPrice, row.drawPrice)
  return (
    <Box sx={{ display: 'grid', rowGap: '2px', fontSize: '13px', lineHeight: 1.45, justifyContent: 'start' }}>
      <Price value={row.homeTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
        runnerKey: 'ML|HOME', label: `${game.homeTeamName} moneyline`, market: 'Moneyline',
        selection: game.homeTeamName, priceKey: row.homePriceKey, margin
      }} />
      <Price value={row.awayTeamPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
        runnerKey: 'ML|AWAY', label: `${game.awayTeamName} moneyline`, market: 'Moneyline',
        selection: game.awayTeamName, priceKey: row.awayPriceKey, margin
      }} />
      {row.drawPrice !== null && row.drawPrice !== undefined && (
        <Price value={row.drawPrice} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
          runnerKey: 'ML|DRAW', label: 'Draw', market: 'Moneyline',
          selection: 'Draw', priceKey: row.drawPriceKey, margin
        }} />
      )}
    </Box>
  )
}

function TeamTotalsCol({ rows, label, usPrices, onOpen, side, movedOf }) {
  if (!rows || !rows.length) {
    return null
  }

  // over and under arrive as separate rows, so the pair that makes up one market has to be found by its
  // line before there is anything to take a margin of
  const byPoints = new Map()
  rows.forEach((row) => {
    const points = totalPoints(row.total)
    if (points) {
      byPoints.set(points, [...(byPoints.get(points) || []), row.price])
    }
  })

  return (
    <Box sx={{ marginBottom: '6px' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3' }}>{label}</Typography>
      <Box sx={ladderSx}>
        {rows.flatMap((row, i) => ([
          <Box key={`l${i}`} sx={labelSx}>{row.total ?? '—'}</Box>,
          <Price key={`p${i}`} value={row.price} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} info={{
            runnerKey: `TEAM_TOTAL|${side}|${row.total}`,
            label: `${label} ${row.total}`,
            market: `${label} total`,
            selection: String(row.total),
            priceKey: row.priceKey,
            margin: marginOf((byPoints.get(totalPoints(row.total)) || []).map(americanToDecimalExact))
          }} />
        ]))}
      </Box>
    </Box>
  )
}

function BookSection({ entry, usPrices, onOpenHistory, moved }) {
  const { bookmaker, sport, league, game } = entry
  const clock = useClock()

  // one price is identified by the book, the period and the runner, the same shape the diff uses
  const movedOf = (runnerKey) => moved.get(`${bookmaker}|${entry.periodType}|${runnerKey}`) || null

  const onOpen = (info, american) => onOpenHistory({
    eventKey: `${game.homeTeamName}|${game.awayTeamName}|${game.eventDateTs}`,
    home: game.homeTeamName,
    away: game.awayTeamName,
    period: entry.periodType,
    periodLabel: PERIOD_LABELS[entry.periodType] || entry.periodType,
    bookmaker: bookmakerLabel(bookmaker),
    runnerKey: info.runnerKey,
    label: info.label,
    market: info.market,
    selection: info.selection,
    margin: info.margin,
    // the book's own handle on this price: without it there is no max stake to ask for
    priceKey: info.priceKey,
    price: americanToDecimal(american)
  })

  const date = clock.date(game.eventDateTs)
  const time = `${clock.time(game.eventDateTs)} ${clock.label}`

  const headSx = {
    display: 'flex',
    padding: '8px 16px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: '#9A9EA3',
    borderBottom: '2px solid #E0E0E0'
  }

  return (
    <Box sx={{ fontFamily: FONT, backgroundColor: 'white', marginBottom: '16px' }}>
      <Box sx={{ padding: '10px 16px 8px' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#6E7378' }}>
          <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>{bookmakerLabel(bookmaker)}</Box>
          {` / ${sport} / ${league}`}
        </Typography>
      </Box>

      <Box sx={headSx}>
        <Box sx={COL.date}>Date</Box>
        <Box sx={COL.time}>Time</Box>
        <Box sx={COL.teams}>Teams</Box>
        <Box sx={{ ...COL.spread, ...cellSx }}>Spread</Box>
        <Box sx={{ ...COL.ml, ...cellSx }}>Moneyline</Box>
        <Box sx={{ ...COL.total, ...cellSx }}>Total</Box>
        <Box sx={{ ...COL.teamTotals, ...cellSx }}>Team Totals</Box>
      </Box>

      <Box sx={{ display: 'flex', padding: '10px 16px', alignItems: 'flex-start' }}>
        <Box sx={{ ...COL.date, fontSize: '13px', color: '#6E7378' }}>{date}</Box>
        <Box sx={{ ...COL.time, fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>{time}</Box>
        <Box sx={COL.teams}>
          <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 600 }}>{game.homeTeamName}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '13.5px', fontWeight: 600 }}>{game.awayTeamName}</Typography>
        </Box>
        <Box sx={{ ...COL.spread, ...cellSx }}>
          <SpreadCol rows={game.spreadDisplayRows} names={{ home: game.homeTeamName, away: game.awayTeamName }} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} />
        </Box>
        <Box sx={{ ...COL.ml, ...cellSx }}>
          <MoneylineCol game={game} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} />
        </Box>
        <Box sx={{ ...COL.total, ...cellSx }}>
          <TotalCol rows={game.totalDisplayRows} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} />
        </Box>
        <Box sx={{ ...COL.teamTotals, ...cellSx }}>
          <TeamTotalsCol rows={game.homeTeamTotals} label={game.homeTeamName} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} side="HOME" />
          <TeamTotalsCol rows={game.awayTeamTotals} label={game.awayTeamName} usPrices={usPrices} onOpen={onOpen} movedOf={movedOf} side="AWAY" />
        </Box>
      </Box>
    </Box>
  )
}

function EventMarkets({ event, book, pollingInterval }) {
  const { marketHost, usPrices, setLastResponseTime, setRequestCount } = useContext(Context)

  /**
   * Which event, taken apart into the three values that name it. A caller that rebuilds its rows every
   * poll — the search board does — hands over a new object each time with the same event in it, and a
   * request keyed on the object would start again from an empty screen twice a second.
   */
  const home = event ? event.home : null
  const away = event ? event.away : null
  const koTime = event ? event.koTime : null
  /**
   * Which book to read, when the caller opened this for one of them: the search board compares against
   * Pinnacle, so a game clicked there is a request for Pinnacle's ladders rather than a pile of sections
   * to scroll past. An event that book does not price at all falls back to showing everything, which is
   * more use than an empty screen.
   */
  const focusBook = book

  const [ books, setBooks ] = useState([])
  const [ error, setError ] = useState('')
  const [ isLoading, setIsLoading ] = useState(true)
  const [ period, setPeriod ] = useState(null)
  const [ historyRequest, setHistoryRequest ] = useState(null)
  const [ moved, setMoved ] = useState(new Map())
  const previousPrices = useRef(new Map())
  const moveTimers = useRef({})

  const fetchEvent = useCallback(async () => {
    if (!home || !away) {
      return
    }
    try {
      const params = new URLSearchParams({
        home,
        away,
        koTime: String(koTime)
      })
      const response = await apiFetch(marketHost, `/api/market/event?${params}`)
      const data = await response.json()

      if (!response.ok) {
        setError(data.error || `Request failed with ${response.status}`)
        return
      }

      setError('')
      // in play books are not recorded and not shown: their kick off time drifts while the match runs
      setBooks((data.books || []).filter((entry) => !/\blive\b/i.test(String(entry.bookmaker || ''))))
      setRequestCount((prev) => prev + 1)
      setLastResponseTime(Date.now())
    } catch (e) {
      setError('Market server is unreachable')
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line
  }, [home, away, koTime, marketHost])

  useEffect(() => {
    setIsLoading(true)
    setBooks([])
    setPeriod(null)
    fetchEvent()
  }, [fetchEvent])

  useInterval(() => {
    fetchEvent()
  }, pollingInterval)

  useEffect(() => () => {
    Object.values(moveTimers.current).forEach(clearTimeout)
  }, [])

  // Same idea as the grid's movement stripe, but done in an effect rather than during render, and with a
  // timer per price so a second move does not get cleared early by the first one's timer.
  useEffect(() => {
    if (!books.length) {
      return
    }
    const current = new Map()
    books.forEach((entry) => {
      const prefix = `${entry.bookmaker}|${entry.periodType}|`
      const game = entry.game
      const put = (runnerKey, price) => {
        if (typeof price === 'number' && price !== 0) {
          current.set(prefix + runnerKey, price)
        }
      }
      const straight = game.threeWayMonelineRow || game.moneylineDisplayRow
      if (straight) {
        put('ML|HOME', straight.homeTeamPrice)
        put('ML|AWAY', straight.awayTeamPrice)
        put('ML|DRAW', straight.drawPrice)
      }
      ;(game.spreadDisplayRows || []).forEach((row) => {
        put(`SPREAD|HOME|${row.homeSpread}`, row.homeTeamPrice)
        put(`SPREAD|AWAY|${row.awaySpread}`, row.awayTeamPrice)
      })
      ;(game.totalDisplayRows || []).forEach((row) => {
        const points = totalPoints(row.homeTotal ?? row.awayTotal)
        put(`TOTAL|OVER|${points}`, row.homeTeamPrice)
        put(`TOTAL|UNDER|${points}`, row.awayTeamPrice)
      })
      ;(game.homeTeamTotals || []).forEach((row) => put(`TEAM_TOTAL|HOME|${row.total}`, row.price))
      ;(game.awayTeamTotals || []).forEach((row) => put(`TEAM_TOTAL|AWAY|${row.total}`, row.price))
    })

    const changes = []
    current.forEach((price, key) => {
      const before = previousPrices.current.get(key)
      if (before !== undefined && before !== price) {
        // american odds cross zero, so a bigger number is not always a better price: compare the payout
        const up = americanToDecimal(price) > americanToDecimal(before)
        changes.push([key, up ? 'up' : 'down'])
      }
    })
    previousPrices.current = current

    if (changes.length) {
      setMoved((prev) => {
        const next = new Map(prev)
        changes.forEach(([key, direction]) => next.set(key, direction))
        return next
      })
      changes.forEach(([key]) => {
        clearTimeout(moveTimers.current[key])
        moveTimers.current[key] = setTimeout(() => {
          delete moveTimers.current[key]
          setMoved((prev) => {
            const next = new Map(prev)
            next.delete(key)
            return next
          })
        }, 3000)
      })
    }
  }, [books])

  // the book is picked before the periods are counted, so a tab cannot offer a half this book never priced
  const visible = useMemo(() => {
    if (!focusBook) {
      return books
    }
    const wanted = focusBook.toLowerCase()
    const only = books.filter((entry) => bookmakerLabel(entry.bookmaker).toLowerCase().includes(wanted))
    return only.length ? only : books
  }, [books, focusBook])

  const periods = useMemo(() => {
    const present = new Set(visible.map((b) => b.periodType))
    const known = PERIOD_ORDER.filter((p) => present.has(p))
    const unknown = [...present].filter((p) => !PERIOD_ORDER.includes(p))
    return [...known, ...unknown]
  }, [visible])

  const activePeriod = period && periods.includes(period) ? period : periods[0]
  const rows = visible.filter((b) => b.periodType === activePeriod)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ padding: '40px', textAlign: 'center' }}>
        <Typography variant="h3" sx={{ color: theme.palette.secondary.main }}>{error}</Typography>
      </Box>
    )
  }

  if (!books.length) {
    return (
      <Box sx={{ padding: '40px', textAlign: 'center' }}>
        <Typography variant="h3">No lines for this event...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '16px' }}>
      {/* a single period is not a choice, and a lone "Game" tab reads as a stray label */}
      {periods.length > 1 && (
        <Box sx={{ display: 'flex', marginBottom: '12px' }}>
          {periods.map((value, index) => {
            const active = value === activePeriod
            return (
              <Typography
                key={value}
                variant="h6"
                onClick={() => setPeriod(value)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '5px 14px',
                  whiteSpace: 'nowrap',
                  // the slant eats 10px on each side, so the plates are pulled back into each other
                  // and only a thin diagonal sliver is left between them
                  marginLeft: index === 0 ? 0 : '-8px',
                  // grey rather than the brand green: these are a place-keeper, not the thing on the
                  // screen worth looking at, and a row of green plates pulled the eye off the prices
                  color: active ? 'white' : '#6E7378',
                  backgroundColor: active ? '#6E7378' : '#ECECEF',
                  clipPath: 'polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%)',
                  '&:hover': { backgroundColor: active ? '#6E7378' : '#DCDCE2' }
                }}
              >
                {PERIOD_LABELS[value] || value}
              </Typography>
            )
          })}
        </Box>
      )}

      {rows.map((entry, index) => (
        <BookSection key={`${entry.bookmaker}|${index}`} entry={entry} usPrices={usPrices} onOpenHistory={setHistoryRequest} moved={moved} />
      ))}
      <PriceHistoryModal request={historyRequest} onClose={() => setHistoryRequest(null)} />
    </Box>
  )
}

export default EventMarkets
